import { LazyUndoRedoAction, UndoRedoAction } from "@/stores/undoRedoStore";
import { useConnectionStore } from "@/stores/workflowConnectionStore";
import { useWorkflowCommentStore, } from "@/stores/workflowEditorCommentStore";
import { useWorkflowStateStore } from "@/stores/workflowEditorStateStore";
import { useWorkflowStepStore } from "@/stores/workflowStepStore";
import { ensureDefined } from "@/utils/assertions";
import { fromSimple } from "../modules/model";
import { cloneStepWithUniqueLabel, getLabelSet } from "./cloneStep";
export class LazySetValueAction extends LazyUndoRedoAction {
    constructor(fromValue, toValue, setValueHandler, showCanvasCallback) {
        super();
        Object.defineProperty(this, "setValueHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "showAttributesCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.fromValue = structuredClone(fromValue);
        this.toValue = structuredClone(toValue);
        this.setValueHandler = setValueHandler;
        this.showAttributesCallback = showCanvasCallback;
    }
    queued() {
        this.setValueHandler(this.toValue);
    }
    changeValue(value) {
        this.toValue = structuredClone(value);
        this.setValueHandler(this.toValue);
    }
    undo() {
        this.showAttributesCallback();
        this.setValueHandler(this.fromValue);
    }
    redo() {
        this.showAttributesCallback();
        this.setValueHandler(this.toValue);
    }
    get name() {
        var _a;
        return (_a = this.internalName) !== null && _a !== void 0 ? _a : "modify workflow";
    }
    set name(name) {
        this.internalName = name;
    }
}
export class SetValueActionHandler {
    constructor(undoRedoStore, setValueHandler, showCanvasCallback, name) {
        Object.defineProperty(this, "undoRedoStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "setValueHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "showAttributesCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lazyAction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.undoRedoStore = undoRedoStore;
        this.setValueHandler = setValueHandler;
        this.showAttributesCallback = showCanvasCallback;
        this.name = name;
    }
    set(from, to) {
        if (this.lazyAction && this.undoRedoStore.isQueued(this.lazyAction)) {
            this.lazyAction.changeValue(to);
        }
        else {
            this.lazyAction = new LazySetValueAction(from, to, this.setValueHandler, this.showAttributesCallback);
            this.lazyAction.name = this.name;
            this.undoRedoStore.applyLazyAction(this.lazyAction);
        }
    }
}
export class CopyIntoWorkflowAction extends UndoRedoAction {
    constructor(workflowId, data, position) {
        super();
        Object.defineProperty(this, "workflowId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "newCommentIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "newStepIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "position", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "commentStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "subAction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "loadWorkflowOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.workflowId = workflowId;
        this.data = structuredClone(data);
        this.position = structuredClone(position);
        this.stepStore = useWorkflowStepStore(this.workflowId);
        this.commentStore = useWorkflowCommentStore(this.workflowId);
        const stateStore = useWorkflowStateStore(this.workflowId);
        this.subAction = new ClearSelectionAction(this.commentStore, stateStore);
        this.loadWorkflowOptions = {
            defaultPosition: this.position,
            appendData: true,
        };
    }
    get name() {
        return `Copy ${this.data.name} into workflow`;
    }
    run() {
        this.subAction.run();
        const commentIdsBefore = new Set(this.commentStore.comments.map((comment) => comment.id));
        const stepIdsBefore = new Set(Object.values(this.stepStore.steps).map((step) => step.id));
        fromSimple(this.workflowId, structuredClone(this.data), structuredClone(this.loadWorkflowOptions));
        const commentIdsAfter = this.commentStore.comments.map((comment) => comment.id);
        const stepIdsAfter = Object.values(this.stepStore.steps).map((step) => step.id);
        this.newCommentIds = commentIdsAfter.filter((id) => !commentIdsBefore.has(id));
        this.newStepIds = stepIdsAfter.filter((id) => !stepIdsBefore.has(id));
    }
    redo() {
        this.subAction.redo();
        fromSimple(this.workflowId, structuredClone(this.data), structuredClone(this.loadWorkflowOptions));
    }
    undo() {
        this.newCommentIds.forEach((id) => this.commentStore.deleteComment(id));
        this.newStepIds.forEach((id) => this.stepStore.removeStep(id));
        this.subAction.undo();
    }
}
export class LazyMoveMultipleAction extends LazyUndoRedoAction {
    get name() {
        return "move multiple nodes";
    }
    constructor(commentStore, stepStore, comments, steps, position, positionTo) {
        super();
        Object.defineProperty(this, "commentStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "comments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "steps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepStartOffsets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "commentStartOffsets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "positionFrom", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "positionTo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.commentStore = commentStore;
        this.stepStore = stepStore;
        this.comments = [...comments];
        this.steps = [...steps.map((step) => ensureDefined(stepStore.getStep(step.id)))];
        this.steps.forEach((step) => {
            this.stepStartOffsets.set(step.id, [step.position.left - position.x, step.position.top - position.y]);
        });
        this.comments.forEach((comment) => {
            this.commentStartOffsets.set(comment.id, [
                comment.position[0] - position.x,
                comment.position[1] - position.y,
            ]);
        });
        this.positionFrom = Object.assign({}, position);
        this.positionTo = positionTo ? Object.assign({}, positionTo) : Object.assign({}, position);
    }
    changePosition(position) {
        this.setPosition(position);
        this.positionTo = Object.assign({}, position);
    }
    setPosition(position) {
        this.steps.forEach((step) => {
            var _a;
            const stepPosition = { left: 0, top: 0 };
            const offset = (_a = this.stepStartOffsets.get(step.id)) !== null && _a !== void 0 ? _a : [0, 0];
            stepPosition.left = position.x + offset[0];
            stepPosition.top = position.y + offset[1];
            this.stepStore.updateStep(Object.assign(Object.assign({}, step), { position: stepPosition }));
        });
        this.comments.forEach((comment) => {
            var _a;
            const offset = (_a = this.commentStartOffsets.get(comment.id)) !== null && _a !== void 0 ? _a : [0, 0];
            const commentPosition = [position.x + offset[0], position.y + offset[1]];
            this.commentStore.changePosition(comment.id, commentPosition);
        });
    }
    queued() {
        this.setPosition(this.positionTo);
    }
    undo() {
        this.setPosition(this.positionFrom);
    }
    redo() {
        this.setPosition(this.positionTo);
    }
}
function getCountName(count, type) {
    if (count === 0) {
        return null;
    }
    else {
        return `${count} ${count === 1 ? type : `${type}s`}`;
    }
}
function getCombinedCountName(stepCount, commentCount) {
    const stepName = getCountName(stepCount, "step");
    const commentName = getCountName(commentCount, "comment");
    if (stepName && commentName) {
        return `${stepName} and ${commentName}`;
    }
    else {
        return (stepName !== null && stepName !== void 0 ? stepName : commentName);
    }
}
export class ClearSelectionAction extends UndoRedoAction {
    constructor(commentStore, stateStore) {
        super();
        Object.defineProperty(this, "commentStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "selectionState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.commentStore = commentStore;
        this.stateStore = stateStore;
        this.selectionState = {
            comments: [...this.commentStore.multiSelectedCommentIds],
            steps: [...this.stateStore.multiSelectedStepIds],
        };
    }
    get name() {
        return "clear selection";
    }
    run() {
        this.commentStore.clearMultiSelectedComments();
        this.stateStore.clearStepMultiSelection();
    }
    undo() {
        this.selectionState.comments.forEach((id) => this.commentStore.setCommentMultiSelected(id, true));
        this.selectionState.steps.forEach((id) => this.stateStore.setStepMultiSelected(id, true));
    }
}
class ModifySelectionAction extends UndoRedoAction {
    constructor(commentStore, stateStore, selection) {
        super();
        Object.defineProperty(this, "commentStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "selection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.commentStore = commentStore;
        this.stateStore = stateStore;
        this.selection = selection;
    }
    run() {
        this.selection.comments.forEach((id) => this.commentStore.setCommentMultiSelected(id, this.addToSelection));
        this.selection.steps.forEach((id) => this.stateStore.setStepMultiSelected(id, this.addToSelection));
    }
    undo() {
        this.selection.comments.forEach((id) => this.commentStore.setCommentMultiSelected(id, !this.addToSelection));
        this.selection.steps.forEach((id) => this.stateStore.setStepMultiSelected(id, !this.addToSelection));
    }
}
export class AddToSelectionAction extends ModifySelectionAction {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "addToSelection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
    }
    get name() {
        const combinedCountName = getCombinedCountName(this.selection.steps.length, this.selection.comments.length);
        return `add ${combinedCountName} to selection`;
    }
}
export class RemoveFromSelectionAction extends ModifySelectionAction {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "addToSelection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
    }
    get name() {
        const combinedCountName = getCombinedCountName(this.selection.steps.length, this.selection.comments.length);
        return `remove ${combinedCountName} from selection`;
    }
}
export class DuplicateSelectionAction extends CopyIntoWorkflowAction {
    constructor(workflowId) {
        const stateStore = useWorkflowStateStore(workflowId);
        const commentStore = useWorkflowCommentStore(workflowId);
        const stepStore = useWorkflowStepStore(workflowId);
        const commentIds = [...commentStore.multiSelectedCommentIds];
        const stepIds = [...stateStore.multiSelectedStepIds];
        const comments = commentIds.map((id) => structuredClone(ensureDefined(commentStore.commentsRecord[id])));
        const labelSet = getLabelSet(stepStore);
        const steps = Object.fromEntries(stepIds.map((id) => [id, cloneStepWithUniqueLabel(ensureDefined(stepStore.steps[id]), labelSet)]));
        const partialWorkflow = { comments, steps, name: "" };
        super(workflowId, partialWorkflow, { left: 100, top: 200 });
    }
    get name() {
        return "duplicate selection";
    }
}
export class DeleteSelectionAction extends UndoRedoAction {
    constructor(workflowId) {
        super();
        Object.defineProperty(this, "storedSelectionAction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connectionStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "storedConnections", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stateStore = useWorkflowStateStore(workflowId);
        this.connectionStore = useConnectionStore(workflowId);
        this.storedSelectionAction = new DuplicateSelectionAction(workflowId);
        this.storedSelectionAction.position = { top: 0, left: 0 };
        this.storedSelectionAction.loadWorkflowOptions = {
            appendData: true,
            reassignIds: false,
            createConnections: false,
        };
        const connectionsForSteps = this.stepIds.flatMap((id) => this.connectionStore.getConnectionsForStep(id));
        this.storedConnections = structuredClone(new Set(connectionsForSteps));
    }
    get name() {
        return "delete selection";
    }
    get commentIds() {
        return this.storedSelectionAction.data.comments.map((comment) => comment.id);
    }
    get stepIds() {
        return Object.values(this.storedSelectionAction.data.steps).map((step) => step.id);
    }
    get commentStore() {
        return this.storedSelectionAction.commentStore;
    }
    get stepStore() {
        return this.storedSelectionAction.stepStore;
    }
    run() {
        this.commentIds.forEach((id) => {
            this.commentStore.deleteComment(id);
        });
        this.commentStore.clearMultiSelectedComments();
        this.stepIds.forEach((id) => {
            this.stepStore.removeStep(id);
        });
        this.stateStore.clearStepMultiSelection();
    }
    undo() {
        this.storedSelectionAction.redo();
        this.storedConnections.forEach((connection) => this.connectionStore.addConnection(structuredClone(connection)));
    }
}
